import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import EmptyState from '../components/EmptyState';
import Tabs from '../components/Tabs';
import AddStudentDrawer from '../components/students/AddStudentDrawer';
import StudentToolbar from '../components/students/StudentToolbar';
import StudentTable from '../components/students/StudentTable';
import TagsTab from '../components/students/tags/TagsTab';
import DeleteStudentsDialog from '../components/students/DeleteStudentsDialog';
import { ALL_COLUMNS } from '../components/students/columns';
import useStudentList from '../hooks/useStudentList';
import useNames from '../hooks/useNames';
import { useStudents } from '../data/useStudents';
import { useFamilies } from '../data/useFamilies';
import { useMakeup } from '../data/useMakeup';
import { useEvents } from '../data/useEvents';
import { useTags } from '../data/useTags';
import { useLocale, useMoneyFormat } from '../context/LocaleContext';
import { familyDisplayName, uncertainDeletionMessage } from '../domain/people';
import { studentTagLabels } from '../domain/tags';
import s from '../components/students/students.module.css';
import { ROSTER_WORDING, bulkStatusPatch } from '../domain/roster';
import { READING, READING_WORDING as RW, reading } from '../domain/reading';

// Students list (SPEC.md §1.1).
//
// Assembly: what is on screen is useStudentList, the controls are
// StudentToolbar, and the table renders itself.

const TABS = ['Students', 'Tags'];

export default function StudentsPage() {
  const {
    students, loading, failed, updateStudent, deleteStudents,
  } = useStudents();
  const { byId: familyById, billingContactFor } = useFamilies();
  const { summaryFor } = useMakeup();
  const { occurrencesBetween } = useEvents();
  const {
    tags, loaded: tagsLoaded, failed: tagsFailed, saveTag, removeTag,
  } = useTags();
  const names = useNames();
  const formatMoney = useMoneyFormat();
  const { decimalSeparator, formatDate } = useLocale();
  const navigate = useNavigate();

  const list = useStudentList({ students, occurrencesBetween });

  const [tab, setTab] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const applyToSelected = async (patch) => {
    setBusy(true);
    try {
      // Sequential rather than batched: these are independent single-document
      // edits, and one failing should not silently roll back the others.
      for (const id of list.selected) await updateStudent(id, patch);
      list.clearSelection();
    } finally { setBusy(false); }
  };

  // Whether a student has history — and so whether `Delete Students` deletes
  // or retires them — is decided by `deleteStudents` in src/data/useStudents.jsx
  // (#318), which asks the server, one `limit(1)` query per collection per
  // student, before it writes: this page's in-memory windows are bounded, and a
  // window at its limit cannot tell a student without history from one whose
  // history it did not load. `STUDENT_REFERENCES` there is the list of what
  // counts, including the two that are ours rather than MMS's.
  //
  // A student the probe could not answer for is retired rather than deleted
  // (#408) — the safe direction, not the one pressed — and the page says so:
  // `plan.uncertain`, worded by `uncertainDeletionMessage` in domain/people.js
  // beside the confirm copy.
  //
  // Both are cleared on the way in, so a second delete does not run under the
  // first one's count or open under its notice. The progress line (#331) is a
  // knowing divergence from MMS that `DeleteStudentsDialog` argues.
  const [deleteProgress, setDeleteProgress] = useState(null);
  const [deleteNotice, setDeleteNotice] = useState(null);

  const deleteSelected = async () => {
    setBusy(true);
    setDeleteProgress(null);
    setDeleteNotice(null);
    try {
      const plan = await deleteStudents({
        ids: [...list.selected], onProgress: setDeleteProgress,
      });
      setDeleteNotice(uncertainDeletionMessage(
        plan?.uncertain?.length || 0, plan?.uncertainBecause || 'history',
      ));
      list.clearSelection();
    } finally { setBusy(false); setDeleteProgress(null); setDeleteOpen(false); }
  };

  // The things a student document cannot answer about itself — including how
  // this studio writes money, which comes from the locale setting rather than
  // from the student. columns.js is not a component, so it is handed the
  // formatter instead of asking for it.
  const cellContext = {
    familyName: (id) => familyDisplayName(familyById(id)),
    // The Family Contact column: the family's billing contact (#792).
    billingContact: billingContactFor,
    makeupSummary: summaryFor,
    nextLesson: list.nextLesson,
    formatMoney,
    // And how it writes a decimal: the make-up credits column's `1,5` (#549).
    decimalSeparator,
    // And how it writes a date: the Student Since column (#587).
    formatDate,
    // The two tag columns, from the managed records and the student's own
    // legacy strings at once (`studentTagLabels` in domain/tags.js).
    tagLabels: (student, kind) => studentTagLabels(student, tags, kind).join(', '),
  };

  const visibleColumns = ALL_COLUMNS.filter((c) => list.columnIds.has(c.id));
  // Unread, empty or not (#637): the empty state below is drawn from this,
  // and draws the loading state while the roster is unread.
  const roster = reading(!loading, students.length, failed);

  return (
    <AppShell title="Students">
      <div className={s.page}>
        <Tabs tabs={TABS} current={tab} onChange={setTab} />

        {tab === 1 ? (
          <TagsTab
            loaded={tagsLoaded}
            failed={tagsFailed}
            tags={tags}
            students={students}
            nameOf={names.student}
            onSave={saveTag}
            onRemove={removeTag}
          />
        ) : (
          <>
            <StudentToolbar
              count={list.rows.length}
              countKnown={roster === READING.empty || roster === READING.some}
              statusFilter={list.statusFilter}
              onToggleStatusFilter={list.toggleStatusFilter}
              onSetStatusFilter={list.setStatusFilter}
              search={list.search}
              onSearchChange={list.setSearch}
              sortAsc={list.sortAsc}
              onToggleSort={list.toggleSort}
              columnIds={list.columnIds}
              onToggleColumn={list.toggleColumn}
              selectedCount={list.selected.size}
              busy={busy}
              onApplyStatus={(status) => applyToSelected(bulkStatusPatch(status))}
              onDeleteSelected={() => setDeleteOpen(true)}
              onAddStudent={() => setAddOpen(true)}
            />

            {/* Above the table rather than inside the dialog, which is closed
                by the time this is known; `role="status"` so a screen reader
                hears it without the focus moving. Dismissed by the next delete:
                it describes a change visible in the rows beneath it. */}
            {deleteNotice ? (
              <div className={s.notice} role="status">{deleteNotice}</div>
            ) : null}

            {roster === READING.unread || roster === READING.failed ? (
              <EmptyState reading={roster} />
            ) : roster === READING.empty ? (
              <div className={s.card}>
                <EmptyState
                  reading={roster}
                  title={RW['students.emptyTitle']}
                  description={RW['students.emptyDescription']}
                  actionLabel={ROSTER_WORDING.addNew}
                  onAction={() => setAddOpen(true)}
                />
              </div>
            ) : (
              <StudentTable
                rows={list.rows}
                columns={visibleColumns}
                selected={list.selected}
                allSelected={list.allSelected}
                onSelectAll={list.selectAll}
                onTick={list.toggleRow}
                onOpen={(id) => navigate(`/students/${id}`)}
                cellContext={cellContext}
              />
            )}
          </>
        )}
      </div>

      <AddStudentDrawer open={addOpen} onClose={() => setAddOpen(false)} />

      <DeleteStudentsDialog
        open={deleteOpen}
        busy={busy}
        progress={deleteProgress}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={deleteSelected}
      />
    </AppShell>
  );
}
